/**
 * What a browser that has never played sees instead of the home screen.
 *
 * THE PROBLEM THIS SOLVES. The guided round already existed, and it was good —
 * one offline practice match with a plain line at each beat, learning
 * bank-or-push by doing it once at zero stakes. It was also a card among five
 * other buttons, on a screen with a rules panel, a mode grid, a difficulty
 * grid, a course row, a duel panel and a name field. Anybody who tapped the
 * big primary button — which is what a big primary button is for — started a
 * real match having never seen a coaching line, and playtesters reported
 * exactly that: two or three matches spent working the game out.
 *
 * So on a first visit there is no menu. There is one sentence and one button,
 * and the button starts the guided round. The menu is what you come back to
 * afterwards, by which point every word on it means something.
 *
 * WHY THIS IS NOT A MODAL TOUR. Because a modal tour is the thing everybody
 * skips, and skipping it costs nothing, so everybody does. This is not an
 * explanation of the game placed in front of the game — it IS the game, forty
 * seconds of it, with the stakes set to zero.
 *
 * SCOPED TO THE HOME ROUTE, and that matters more than it looks. A duel link,
 * the daily and the city route all reach a first-time browser too, and every
 * one of them is a person who came here to do a specific thing. Intercepting
 * those would break a shared link, which is the one thing this game has going
 * for it. `App` only renders this on `home`; there is no global redirect.
 */

import { TIER_CONFIG } from '@geogame/shared';

import { useEntrance } from '../lib/entrance';
import { navigate } from '../lib/router';
import { useGame } from '../store/gameStore';

export interface FirstRunScreenProps {
  /** Give up on the intro and show the menu. Never starts a match. */
  readonly onSkip: () => void;
}

export function FirstRunScreen({ onSkip }: FirstRunScreenProps): React.JSX.Element {
  const startMatch = useGame((s) => s.startMatch);

  /*
   * EXPLORER, COUNTRIES, PRACTICE, COACHED — every one of them fixed.
   *
   * This screen asks no questions, which is the entire point of it. Explorer
   * because three pinpoint attempts and a generous tap radius are the
   * difference between learning the loop and bouncing off it; practice because
   * nothing here should be ranked or raced; coached because that is what makes
   * it a tutorial rather than a match.
   */
  const begin = (): void => {
    startMatch({ mode: 'practice', tier: 'explorer', gameMode: 'country', coached: true });
  };

  // Quoted from the tier config rather than typed out, so a tuning change
  // cannot leave this promise lying to somebody on their very first screen.
  const tries = TIER_CONFIG.explorer.pinpointAttempts.maxAttempts;

  // The true landing screen for a first visit, so it gets the entrance the home
  // screen gets. Shared claim: dismissing the intro does not replay it.
  const entering = useEntrance();

  return (
    <main className="gg-screen gg-firstrun" data-entering={entering ? 'true' : undefined}>
      <header className="gg-hero">
        <h1 className="gg-title">Worldly</h1>
        <p className="gg-firstrun-pitch">
          Find a country on the globe to win points. Then <strong>keep them</strong>, or{' '}
          <strong>bet the lot</strong> on a question about that same country, for more.
        </p>
      </header>

      <ol className="gg-firstrun-beats">
        <li>
          <span className="gg-firstrun-beat-n" aria-hidden="true">
            1
          </span>
          <span>
            You are shown a country. Tap where you think it is. You get {tries} tries, and the
            closer you land the more you win.
          </span>
        </li>
        <li>
          <span className="gg-firstrun-beat-n" aria-hidden="true">
            2
          </span>
          <span>
            Bank what you won and the round ends safely. Or push, and bet it on one trivia question
            about that country.
          </span>
        </li>
        <li>
          <span className="gg-firstrun-beat-n" aria-hidden="true">
            3
          </span>
          <span>
            Right, and it multiplies and you choose again. Wrong, and the round ends at nothing. How
            far you climb is up to you.
          </span>
        </li>
      </ol>

      <div className="gg-firstrun-actions">
        <button type="button" className="gg-btn gg-btn-primary gg-btn-xl" onClick={begin}>
          Play a round
        </button>
        <p className="gg-note">
          Against nobody, with a line of coaching at each step and a relaxed clock on the first
          round so you can read as you go. Nothing to lose and no account needed.
        </p>
        <button type="button" className="gg-firstrun-skip" onClick={onSkip}>
          Skip. I already know how this works
        </button>
        {/*
          THE OTHER KIND OF ARRIVAL.
          ==============================================================
          Not everybody who has never played on THIS browser has never
          played. Somebody with an account opening the game on a new phone
          got a tutorial and a skip link, neither of which is what they
          came for, and no way to say so -- their coverage, their scores
          and their rivalries all sat behind a door with no handle on this
          side. It costs one line here and it is the only place they will
          look.
        */}
        <button
          type="button"
          className="gg-firstrun-skip"
          onClick={() => navigate({ name: 'signin' })}
        >
          Already have an account? Sign in
        </button>
      </div>
    </main>
  );
}
